/**
 * Feedback API utilities
 * Handles submission and retrieval of customer feedback
 *
 * Production: Sends to Lambda → n8n → NetSuite
 * Development: Uses localStorage for testing
 */

import type {
  FeedbackFormData,
  FeedbackItem,
  FeedbackSubmitResponse,
  FeedbackListResponse,
} from '../types';

// API endpoint - in production, CloudFront routes /api/feedback to Lambda
const FEEDBACK_API_URL = import.meta.env.VITE_FEEDBACK_API_URL || '/api/feedback';
const EVENTS_API_URL = import.meta.env.VITE_EVENTS_API_URL || '/api/events';
const USE_MOCK = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Local storage key for dev mode
const STORAGE_KEY = 'hul_proposal_feedback';

/**
 * Generate a unique ID for feedback items
 */
function generateId(): string {
  return `fb-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get feedback items from local storage (dev mode)
 */
function getLocalFeedback(proposalId: string): FeedbackItem[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];

  const allFeedback: FeedbackItem[] = JSON.parse(stored);
  return allFeedback.filter(item => item.proposalId === proposalId);
}

/**
 * Save feedback item to local storage (dev mode)
 */
function saveLocalFeedback(item: FeedbackItem): void {
  const stored = localStorage.getItem(STORAGE_KEY);
  const allFeedback: FeedbackItem[] = stored ? JSON.parse(stored) : [];
  allFeedback.push(item);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allFeedback));
}

/**
 * Submit feedback to the API or local storage
 *
 * @param proposalId - NetSuite proposal internal ID
 * @param version - Proposal version number
 * @param data - Feedback form data
 * @param customerName - Optional customer name
 * @param customerEmail - Optional customer email
 */
export async function submitFeedback(
  proposalId: string,
  version: number,
  data: FeedbackFormData,
  customerName?: string,
  customerEmail?: string
): Promise<FeedbackSubmitResponse> {
  const feedbackItem: FeedbackItem = {
    id: generateId(),
    proposalId,
    section: data.section,
    lineNumber: data.lineNumber,
    comment: data.comment,
    sentiment: data.sentiment,
    createdAt: new Date().toISOString(),
    customerName,
    customerEmail,
    isRead: false,
  };

  if (USE_MOCK) {
    // Dev mode: use local storage
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network
    saveLocalFeedback(feedbackItem);
    return { success: true, feedbackId: feedbackItem.id };
  }

  // Production: send to Lambda → n8n → NetSuite
  try {
    // Lambda expects: { proposalId, section, lineNumber, comment, version }
    const payload = {
      proposalId,
      section: data.section,
      lineNumber: data.lineNumber || null,
      comment: data.comment,
      version,
    };

    const response = await fetch(FEEDBACK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to submit feedback');
    }

    const result = await response.json();
    return { success: true, feedbackId: result.feedbackId };
  } catch (error) {
    console.error('Feedback submission error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit feedback',
    };
  }
}

/**
 * Fetch all feedback for a proposal
 * Returns feedback items including salesperson responses
 *
 * @param proposalId - NetSuite proposal internal ID
 * @param accessToken - Access token for validation
 */
export async function fetchFeedback(
  proposalId: string,
  accessToken?: string
): Promise<FeedbackListResponse> {
  if (USE_MOCK) {
    await new Promise(resolve => setTimeout(resolve, 200));
    const items = getLocalFeedback(proposalId);
    return { success: true, items };
  }

  // In production, fetch from API if we have an access token
  if (!accessToken) {
    // No token available - return empty (backwards compatible)
    return { success: true, items: [] };
  }

  try {
    const url = `${FEEDBACK_API_URL}?proposalId=${encodeURIComponent(proposalId)}&token=${encodeURIComponent(accessToken)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Failed to fetch feedback:', errorData.error || response.statusText);
      return { success: false, error: errorData.error || 'Failed to fetch feedback' };
    }

    const result = await response.json();

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to fetch feedback' };
    }

    // Map NetSuite feedback format to frontend FeedbackItem format
    const items: FeedbackItem[] = (result.feedback || []).map((fb: {
      id: string;
      section: string;
      lineNumber?: number | null;
      comment: string;
      sentiment?: string;
      submitted?: string;
      addressed?: boolean;
      response?: string | null;
      respondedAt?: string | null;
    }) => ({
      id: fb.id,
      proposalId,
      section: fb.section as FeedbackItem['section'],
      lineNumber: fb.lineNumber || undefined,
      comment: fb.comment,
      sentiment: (fb.sentiment || 'neutral') as FeedbackItem['sentiment'],
      createdAt: fb.submitted || new Date().toISOString(),
      isRead: fb.addressed || false,
      response: fb.response || undefined,
      respondedAt: fb.respondedAt || undefined,
    }));

    return { success: true, items };
  } catch (error) {
    console.error('Feedback fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch feedback',
    };
  }
}

/**
 * Track engagement events (views, interactions)
 */
export type EventType = 'first_view' | 'section_view' | 'time_on_page' | 'accept_clicked' | 'contact_clicked';

export async function trackEvent(
  proposalId: string,
  eventType: EventType,
  data?: Record<string, unknown>
): Promise<void> {
  if (USE_MOCK) {
    console.log('[Mock Event]', { proposalId, eventType, data });
    return;
  }

  try {
    await fetch(EVENTS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        proposalId,
        eventType,
        data: data || {},
      }),
    });
  } catch (error) {
    // Don't throw - events are non-critical
    console.error('Event tracking error:', error);
  }
}

/**
 * Get display label for feedback section
 */
export function getSectionLabel(section: string): string {
  const labels: Record<string, string> = {
    hero: 'Proposal Overview',
    equipment: 'Equipment',
    addons: 'Add-ons & Accessories',
    services: 'Services',
    tradeIn: 'Trade-In',
    financing: 'Financing',
    pricing: 'Pricing',
    general: 'General',
  };
  return labels[section] || section;
}

/**
 * Get sentiment icon class
 */
export function getSentimentInfo(sentiment: string): { icon: string; color: string; label: string } {
  switch (sentiment) {
    case 'positive':
      return { icon: '👍', color: 'text-green-600', label: 'Looks Good' };
    case 'concern':
      return { icon: '⚠️', color: 'text-amber-600', label: 'Have Concerns' };
    default:
      return { icon: '💬', color: 'text-blue-600', label: 'Question/Comment' };
  }
}
